/** Client-side ordering for `DataTable`. Kept apart from the component so the component file exports
 * only components (fast refresh) and so a screen can sort its own rows the same way. */

import { readerLocale } from "../../lib/format";

export type SortDirection = "asc" | "desc";
export interface SortState {
  columnId: string;
  direction: SortDirection;
}

type Key = string | number | null | undefined;

function isNone(value: Key): boolean {
  return value === null || value === undefined || value === "";
}

function compare(a: Key, b: Key): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  // The reader's own collation (the one locale context, `lib/format.ts`), not a literal market's.
  return String(a).localeCompare(String(b), readerLocale(), { numeric: true, sensitivity: "base" });
}

/** Sorts a copy by `accessor`. Stable, numbers numerically, text naturally ("2" before "10"), and
 * empty values last in both directions (a blank is not the smallest figure). */
export function sortRows<T>(
  rows: readonly T[],
  accessor: ((row: T) => Key) | undefined,
  direction: SortDirection,
): T[] {
  if (!accessor) return [...rows];
  const sign = direction === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, key: accessor(row) }))
    .sort((x, y) => {
      const xNone = isNone(x.key);
      const yNone = isNone(y.key);
      if (xNone || yNone) return (xNone ? 1 : 0) - (yNone ? 1 : 0) || x.index - y.index;
      return sign * compare(x.key, y.key) || x.index - y.index;
    })
    .map((entry) => entry.row);
}

/** Sorts a copy by several levels at once: `sorts[0]` is the primary key, `sorts[1]` breaks its ties,
 * and so on. `sortRows` is stable, so applying the levels from the last to the first gives exactly
 * that. A level whose column has no accessor is skipped (a column the page cannot order). */
export function sortRowsBy<T>(
  rows: readonly T[],
  sorts: readonly SortState[],
  accessors: Readonly<Record<string, ((row: T) => Key) | undefined>>,
): T[] {
  let out = [...rows];
  for (let level = sorts.length - 1; level >= 0; level -= 1) {
    const sort = sorts[level] as SortState;
    const accessor = accessors[sort.columnId];
    if (accessor) out = sortRows(out, accessor, sort.direction);
  }
  return out;
}
